import { ToolCallComplete } from "../streaming/anthropic-stream-handler.types";
import {
  DelayedStreamAdapter,
  TypingOptions,
} from "../streaming/delayed-stream-adapter";
import {
  getAssistantMessageContent,
  isAssistantMessage,
  Message,
  ToolContentBlock,
} from "../streaming/streaming-message-types";

import { DisplayQueue } from "./DisplayQueue";
import { TypedEventEmitter } from "./emitter";
import { Logger, NoopLogger } from "./logger";
import { MessageStore } from "./MessageStore";
import { ConversationProvider } from "./provider";
import { EngineEvent, reduce as reduceState } from "./StateMachine";
import { ToolTracker } from "./ToolTracker";
import {
  ConversationEngine,
  ConversationEngineEventMap,
  ConversationEngineState,
} from "./types";
// reducer import moved above to satisfy lint order
// logger import moved above to satisfy lint order

type EngineOptions = {
  provider: ConversationProvider;
  typing?: TypingOptions;
  enableAnimations?: boolean;
  logger?: Logger;
};

export class BasicConversationEngine
  extends TypedEventEmitter<ConversationEngineEventMap>
  implements ConversationEngine
{
  private messages: Message[] = [];
  private state: ConversationEngineState = {
    kind: "awaiting_user_input",
    isBusy: false,
  };

  private displayQueue: DisplayQueue;
  private sessionToken = 0;
  private aborted = false;
  // Tool state tracker
  private tools = new ToolTracker();
  private store = new MessageStore({
    getMessages: () => this.messages,
    setMessages: (next) => {
      this.messages = next;
    },
    emitMessagesUpdated: (messages) =>
      this.emit("messagesUpdated", { messages }),
  });

  constructor(private readonly opts: EngineOptions) {
    super();
    this.displayQueue = new DisplayQueue({
      getMessages: () => this.messages,
      setMessages: (next) => {
        this.messages = next;
      },
      emitMessagesUpdated: (messages) => {
        this.emit("messagesUpdated", { messages });
      },
      enableAnimations: !!this.opts.enableAnimations,
    });
  }

  getState(): ConversationEngineState {
    return this.state;
  }

  getMessages(): Message[] {
    return this.messages;
  }

  seedHistory(messages: Message[]): void {
    if (!Array.isArray(messages) || messages.length === 0) return;
    const sanitized = messages
      .filter((m) => m && m.displayState?.kind === "displayed")
      .map((m) => ({ ...m, displayState: m.displayState } as Message));
    if (sanitized.length === 0) return;
    this.messages = [...this.messages, ...sanitized];
    this.emit("messagesUpdated", { messages: [...this.messages] });
    this.setState({ kind: "awaiting_user_input", isBusy: false });
  }

  async sendUserMessage(userText: string): Promise<void> {
    if (!userText || !userText.trim()) return;
    if (this.state.kind !== "awaiting_user_input") {
      const logger = this.opts.logger ?? new NoopLogger();
      logger.warn(`Ignoring user message while in state: ${this.state.kind}`);
      return;
    }

    this.store.appendUser(userText);
    this.applyEvent({ type: "UserMessageSent" });

    // Start stream
    this.emit("streamStarted", { userText });

    try {
      const response = await this.opts.provider.createUserStream(userText, [
        ...this.messages,
      ]);

      // simulate AI typing delay
      await new Promise((resolve) => setTimeout(resolve, 300));
      await this.beginAssistantStream(response);
    } catch (error) {
      this.failWith(error);
    }
  }

  async continueWithToolResults(content: ToolContentBlock[]): Promise<void> {
    try {
      if (this.state.kind !== "awaiting_ai_continuation") {
        try {
          // eslint-disable-next-line no-console
          console.warn(
            `[AdonisEngine] continueWithToolResults called from state ${this.state.kind}`
          );
        } catch {
          // ignore
        }
      }
      const response = await this.opts.provider.continueWithToolResults(
        content,
        [...this.messages]
      );
      await this.beginAssistantStream(response, /* hidden */ true);
    } catch (error) {
      this.failWith(error);
    }
  }

  abort(): void {
    this.displayQueue.clear();
    this.sessionToken += 1;
    this.aborted = true;
    // Transition to aborted once adapters stop emitting
    this.setState({ kind: "aborted", isBusy: false });
  }

  reset(): void {
    this.displayQueue.clear();
    this.sessionToken += 1;
    this.aborted = false;
    this.tools.clear();
    this.messages = [];
    this.emit("messagesUpdated", { messages: [] });
    this.setState({ kind: "awaiting_user_input", isBusy: false });
  }

  // Internals

  private setState(next: ConversationEngineState): void {
    if (this.state.kind === next.kind) {
      return;
    }
    if (!this.isValidTransition(this.state.kind, next.kind)) {
      const logger = this.opts.logger ?? new NoopLogger();
      logger.warn(
        `Invalid state transition: ${this.state.kind} -> ${next.kind}`
      );
    }
    this.state = next;
    this.emit("stateChanged", { state: next });
  }

  private isValidTransition(
    from: ConversationEngineState["kind"],
    to: ConversationEngineState["kind"]
  ): boolean {
    if (from === to) return true;
    // Error and aborted are universal targets
    if (to === "error" || to === "aborted") return true;
    // Allow settling to idle from anywhere
    if (to === "awaiting_user_input") return true;
    const allowed: Record<
      ConversationEngineState["kind"],
      Set<ConversationEngineState["kind"]>
    > = {
      awaiting_user_input: new Set(["preparing_stream"]),
      preparing_stream: new Set(["streaming_text", "streaming_tool_calls"]),
      streaming_text: new Set(["streaming_tool_calls", "post_streaming"]),
      streaming_tool_calls: new Set(["streaming_text", "post_streaming"]),
      post_streaming: new Set([
        "awaiting_tool_responses",
        "awaiting_user_input",
      ]),
      awaiting_tool_responses: new Set([
        "awaiting_ai_continuation",
        "awaiting_user_input",
      ]),
      awaiting_ai_continuation: new Set([
        "streaming_text",
        "streaming_tool_calls",
      ]),
      aborted: new Set(["awaiting_user_input"]),
      error: new Set(["awaiting_user_input", "aborted"]),
    };
    const nexts = allowed[from];
    return nexts ? nexts.has(to) : false;
  }

  private failWith(err: unknown): void {
    const message = err instanceof Error ? err.message : String(err);
    this.setState({
      kind: "error",
      isBusy: false,
      error: { message, cause: err },
    });
    this.emit("error", { error: { message, cause: err } });
  }

  // Reducer integration
  private applyEvent(evt: EngineEvent): void {
    const next = reduceState(this.state, evt);
    if (next.kind !== this.state.kind || next.isBusy !== this.state.isBusy) {
      this.setState(next);
    }
  }

  private createAdapterForAssistant(
    messageIndex: number,
    startPaused: boolean
  ) {
    const adapter = new DelayedStreamAdapter({
      startPaused: startPaused || !this.opts.enableAnimations,
      typingOpts: this.opts.enableAnimations ? this.opts.typing : undefined,
      display: {
        onDisplayTextUpdate: (visibleText) => {
          this.store.updateAssistantDisplay(messageIndex, visibleText);
        },
        onDisplayComplete: () => {
          if (this.tools.hasStreamingRequests()) {
            this.setState({ kind: "streaming_tool_calls", isBusy: true });
          }
          this.displayQueue.onActiveDisplayComplete(messageIndex);
          this.handleAssistantPostDisplayFlow(messageIndex);
        },
        onTextContentBlockStop: () => {
          // Mark current assistant message as fully displayed
          const msgs = this.messages;
          const assistantMessage = msgs[messageIndex];

          if (assistantMessage && assistantMessage.role === "assistant") {
            if (assistantMessage.displayState?.kind === "displaying") {
              msgs[messageIndex] = {
                ...assistantMessage,
                displayState: { kind: "displayed" },
              };
              this.emit("messagesUpdated", { messages: [...msgs] });
            }
          }

          // If tool requests are still streaming, reflect that state now
          if (this.tools.hasStreamingRequests()) {
            this.setState({ kind: "streaming_tool_calls", isBusy: true });
          }
        },
        onEvent: (eventType, data) => {
          if (eventType !== "message_start") return;
          try {
            const eventMessageId = (data as { message?: { id?: string } })
              ?.message?.id;
            if (!eventMessageId) return;
            this.store.setAssistantMessageId(messageIndex, eventMessageId);
          } catch {
            // ignore
          }
        },
        onToolCallRequestStart: (start) => {
          // A tool request input stream has begun
          // Record streaming status for this tool
          this.tools.startRequest(start.id, start.name);
          this.store.reflectToolUseStreaming(
            messageIndex,
            start.id,
            start.name
          );
          this.emit("toolCallStart", { id: start.id, name: start.name });
          // Track active tools via ToolTracker; keep current kind here.

          const currentMessage = this.messages[messageIndex];
          if (!isAssistantMessage(currentMessage)) return;
          // If the assistant has not displayed any text yet, switch immediately.
          const hasVisibleText =
            currentMessage.displayState.kind === "displaying" &&
            !!getAssistantMessageContent(currentMessage).trim().length;

          this.setState({
            kind: hasVisibleText ? this.state.kind : "streaming_tool_calls",
            isBusy: true,
          });
        },
        onToolCallRequestComplete: (toolCall) => {
          // Tool request input stream finished for this tool_use
          // Promote tool from streaming -> pending execution
          this.tools.markPendingExec(toolCall.id, toolCall.name);
          this.store.addToolCallRequestComplete(
            messageIndex,
            toolCall as ToolCallComplete
          );
          this.emit("toolCallComplete", {
            id: toolCall.id,
            name: toolCall.name,
            args: toolCall.arguments,
          });

          // Execute tool immediately and track promise
          const execPromise = this.opts.provider
            .executeTool(toolCall as ToolCallComplete)
            .then((res) => {
              this.emit("toolExecuted", {
                id: toolCall.id,
                name: toolCall.name,
                status: "success",
                result: res,
              });
              // Remove from active list on completion so if text display is still active,
              // we may skip streaming_tool_calls entirely.
              this.setState({ kind: this.state.kind, isBusy: true });
              this.tools.markDone(toolCall.id);
              return { toolCallId: toolCall.id, result: res } as const;
            })
            .catch((err) => {
              const msg = err instanceof Error ? err.message : String(err);
              this.emit("toolExecuted", {
                id: toolCall.id,
                name: toolCall.name,
                status: "failed",
                error: msg,
              });
              // Remove from active list on failure
              this.setState({ kind: this.state.kind, isBusy: true });
              this.tools.markError(toolCall.id, msg);
              return {
                toolCallId: toolCall.id,
                result: `Error: ${msg}`,
              } as const;
            });
          this.tools.markPendingExec(toolCall.id, toolCall.name, execPromise);
        },
        onStreamComplete: async (result) => {
          const currentAssistant = this.messages[messageIndex];
          const assistantToolCalls =
            currentAssistant && currentAssistant.role === "assistant"
              ? currentAssistant.toolCalls ?? []
              : [];
          this.store.finalizeAssistant(messageIndex, {
            text: result.fullTextContent,
            toolCalls: assistantToolCalls,
            messageId: result.messageId,
          });
          // Do not dispatch AssistantTextCompleted here. The underlying stream
          // has finished, but the UI may still be animating text. We emit
          // AssistantTextCompleted after display completion in
          // handleAssistantPostDisplayFlow.
          if (!this.opts.enableAnimations) {
            const msgs = this.messages;
            const a = msgs[messageIndex];
            if (a && a.role === "assistant") {
              msgs[messageIndex] = {
                ...a,
                displayText: result.fullTextContent,
                displayState: {
                  kind: "displayed",
                  displayText: result.fullTextContent,
                },
              } as Message;
              this.emit("messagesUpdated", { messages: [...msgs] });
            }
          }

          const currentAssistant2 = this.messages[messageIndex];
          const toolIds =
            currentAssistant2 && currentAssistant2.role === "assistant"
              ? (currentAssistant2.toolCalls ?? []).map((t) => t.id)
              : [];
          this.emit("streamCompleted", {
            result: {
              fullText: result.fullTextContent,
              toolIds,
              usage: result.usage,
            },
          });
        },
        onError: (err) => this.failWith(err),
      },
    });
    return adapter;
  }

  private async handleAssistantPostDisplayFlow(
    messageIndex: number
  ): Promise<void> {
    const assistantMessage = this.messages[messageIndex];
    if (!assistantMessage || assistantMessage.role !== "assistant") {
      this.maybeCompleteTurn();
      return;
    }
    // If any assistant display is currently active (e.g., continuation),
    // defer post-stream decisions until that completes.
    const anyAssistantActive = this.messages.some(
      (m) => m.role === "assistant" && m.displayState?.kind === "displaying"
    );
    if (anyAssistantActive) {
      return;
    }

    // If text display completed but the assistant stream hasn't completed yet
    // and tool requests are streaming, reflect streaming_tool_calls now.
    if (!(assistantMessage.displayState.kind === "displayed")) {
      if (this.tools.hasStreamingRequests()) {
        this.setState({ kind: "streaming_tool_calls", isBusy: true });
      }
      return;
    }
    const toolCalls = assistantMessage.toolCalls ?? [];
    // Enter post_streaming only after assistant message streaming completes
    this.applyEvent({ type: "AssistantTextCompleted" });
    if (toolCalls.length === 0) {
      // No tools to wait for; settle turn if possible
      this.maybeCompleteTurn();
      return;
    }
    // After text completes:
    // - If any requests are still streaming, enter streaming_tool_calls and wait for completion
    // - Else go directly to awaiting_tool_responses
    if (this.tools.hasStreamingRequests()) {
      this.applyEvent({ type: "ToolRequestsStillStreaming" });
      return;
    }
    this.applyEvent({ type: "ReadyForToolResponses" });
    try {
      const executedToolResults = await Promise.all(
        toolCalls.map((toolCallItem) => {
          const existing = this.tools.getPromise(toolCallItem.id);
          if (existing) return existing;
          const fallback = this.opts.provider
            .executeTool(toolCallItem as ToolCallComplete)
            .then((res) => {
              this.emit("toolExecuted", {
                id: toolCallItem.id,
                name: toolCallItem.name,
                status: "success",
                result: res,
              });
              this.tools.markDone(toolCallItem.id);
              return { toolCallId: toolCallItem.id, result: res } as const;
            })
            .catch((err) => {
              const msg = err instanceof Error ? err.message : String(err);
              this.emit("toolExecuted", {
                id: toolCallItem.id,
                name: toolCallItem.name,
                status: "failed",
                error: msg,
              });
              this.tools.markError(toolCallItem.id, msg);
              return {
                toolCallId: toolCallItem.id,
                result: `Error: ${msg}`,
              } as const;
            });
          this.tools.markPendingExec(
            toolCallItem.id,
            toolCallItem.name,
            fallback
          );
          return fallback;
        })
      );

      // Insert hidden tool_result message
      const toolResultContent = executedToolResults.map((tr) => ({
        type: "tool_result" as const,
        tool_use_id: tr.toolCallId,
        content: tr.result,
      }));
      // No explicit cleanup needed; ToolTracker clears promises on done/error

      this.messages = [
        ...this.messages,
        {
          role: "tool",
          content: toolResultContent,
          displayText: "",
          displayState: this.opts.enableAnimations
            ? { kind: "hidden" }
            : { kind: "displayed", displayText: "" },
          messageKind: "tool_result",
        } as Message,
      ];
      this.emit("messagesUpdated", { messages: [...this.messages] });

      // Continue conversation with tool results, start next assistant hidden
      this.applyEvent({ type: "ContinuationRequested" });
      const response = await this.opts.provider.continueWithToolResults(
        toolResultContent,
        [...this.messages]
      );
      await this.beginAssistantStream(response, /* hidden */ true);
    } catch (err) {
      this.failWith(err);
    }
  }

  private async beginAssistantStream(
    response: Response,
    hidden = false
  ): Promise<void> {
    try {
      this.sessionToken += 1;
      this.aborted = false;

      if (!response.ok)
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      if (!response.body) throw new Error("ReadableStream not supported");

      const newAssistantIndex = this.store.appendAssistant({ hidden });

      const adapter = this.createAdapterForAssistant(
        newAssistantIndex,
        /* startPaused */ hidden
      );
      if (hidden) {
        // Reveal immediately for no-animations, or if nothing is displaying and animations enabled
        const anyAssistantActive = this.messages.some(
          (message) =>
            message.role === "assistant" &&
            message.displayState?.kind === "displaying"
        );
        const shouldRevealNow =
          !this.opts.enableAnimations ||
          (!anyAssistantActive && this.opts.enableAnimations);
        if (shouldRevealNow) {
          const nextAssistantMessage = this.messages[newAssistantIndex];
          nextAssistantMessage.displayState = this.opts.enableAnimations
            ? {
                kind: "displaying",
                visibleText: "",
              }
            : {
                kind: "displayed",
              };
        }
        // Even when hidden, the assistant stream has started; reflect streaming_text
        this.applyEvent({ type: "AssistantStreamStarted" });
      } else {
        this.applyEvent({ type: "AssistantStreamStarted" });
      }
      this.displayQueue.enqueue(adapter, newAssistantIndex);

      await adapter.processStream(response.body);
    } catch (error) {
      // Surface error in the current assistant message
      const lastAssistantMessage = this.messages[this.messages.length - 1];
      if (lastAssistantMessage && lastAssistantMessage.role === "assistant") {
        const errText = `Error: ${
          error instanceof Error ? error.message : "Unknown error"
        }`;
        lastAssistantMessage.content = [{ type: "text", text: errText }];
        lastAssistantMessage.displayState = {
          kind: "displayed",
        };
        this.emit("messagesUpdated", { messages: [...this.messages] });
      }
      this.failWith(error);
    }
  }

  private maybeCompleteTurn(): void {
    if (this.state.kind === "awaiting_ai_continuation") {
      return;
    }
    const anyAssistantActive = this.messages.some(
      (message) =>
        message.role === "assistant" &&
        message.displayState?.kind === "displaying"
    );
    const allAssistantDisplayed = this.messages.every(
      (message) =>
        message.role !== "assistant" ||
        message.displayState?.kind === "displayed"
    );
    const hasHiddenMessages = this.messages.some(
      (m) => m.displayState?.kind === "hidden"
    );
    const hasPendingTools = this.tools.hasPending();
    const waitingNextAdapter = this.displayQueue.hasPending();

    // Only mark the turn complete when no assistant is active, all assistants
    // have finished displaying, there are no hidden messages, no pending tools,
    // no queued continuation adapter, and we are not in the middle of a tool chain.
    if (
      !anyAssistantActive &&
      allAssistantDisplayed &&
      !hasHiddenMessages &&
      !hasPendingTools &&
      !waitingNextAdapter
    ) {
      this.setState({ kind: "awaiting_user_input", isBusy: false });
    }
  }
}
